import { useEffect, useRef, useState } from "react";
import { UnitObject } from "@common/types/tree-types";
import { useClickOutsideListener } from "@src/hooks/use-click-outside-listener";
import { searchUnitsList } from "./search-units-list";

export interface UnitInputAndPopoverHookReturnTypes {
  popoverRef: React.MutableRefObject<HTMLDivElement>;
  inputValue: string;
  inputOnChange: (value: string) => void;
  matchingUnitsArray: UnitObject[];
  handleClickOnPopover: (unit: UnitObject) => void;
}

export const useUnitInputAndPopoverHook = (
  unit: UnitObject,
  popoverOnClickCallback: (value: UnitObject) => void,
): UnitInputAndPopoverHookReturnTypes => {
  const [inputValue, setInputValue] = useState<string>("");
  const [localUnit, setLocalUnit] = useState<UnitObject>(null);
  const [matchingUnitsArray, setMatchingUnitsArray] = useState<
    UnitObject[] | []
  >([]);

  const popoverRef = useRef<HTMLDivElement>(null);

  const unitToString = (unit: UnitObject) => {
    if (unit === null) {
      return "";
    }
    return `${unit.display_name} - ${unit.description}`;
  };

  const handleClosePopover = () => {
    localUnit ? setInputValue(unitToString(localUnit)) : setInputValue("");
    setMatchingUnitsArray([]);
  };

  useClickOutsideListener(popoverRef, handleClosePopover, true);

  const inputOnChange = (value: string) => {
    setInputValue(value);
  };

  const setLocalInputStringUnit = (unit: UnitObject) => {
    setInputValue(unitToString(unit));
  };

  const handleClickOnPopover = (unit: UnitObject) => {
    setLocalInputStringUnit(unit);
    setLocalUnit(unit);
    popoverOnClickCallback(unit);
    setMatchingUnitsArray([]);
  };

  useEffect(() => {
    const foundUnits = searchUnitsList(inputValue);
    setMatchingUnitsArray(foundUnits ? foundUnits : []);
  }, [inputValue]);

  const unknownUnit = {
    code: -1,
    display_name: "<UNKNOWN>",
    description: "<UNKNOWN>",
  };

  function isUnitAnUnknownUnit(unitToTest, unknownUnit) {
    if (unitToTest == null || typeof unitToTest !== "object") {
      return false;
    }

    const unknownKeys = Object.keys(unknownUnit);

    for (const key of unknownKeys) {
      if (!(key in unitToTest) || unitToTest[key] !== unknownUnit[key]) {
        return false;
      }
    }

    return true;
  }

  useEffect(() => {
    if (unit === null || isUnitAnUnknownUnit(unit, unknownUnit)) {
      setInputValue("");
    } else {
      setLocalInputStringUnit(unit);
      setLocalUnit(unit);
      setMatchingUnitsArray([]);
    }
  }, [unit]);

  return {
    popoverRef,
    inputValue,
    inputOnChange,
    matchingUnitsArray,
    handleClickOnPopover,
  };
};
